/**
 * TalentOS — "Download" the match report: opens a new window with the exact
 * reference HTML/CSS and immediately shows the browser's print dialog, so
 * the user picks "Save as PDF" there -- one click on Download, straight to
 * the print dialog, no intermediate file to open by hand.
 *
 * Went through several attempts the same day chasing two requirements that
 * turned out to be in tension ("one click, no extra steps" AND "same UI as
 * the approved reference file"):
 *  1. Plain-text pdf-lib -- one click, but no colour/visual language at all.
 *  2. Colour-upgraded pdf-lib (real drawRectangle/drawCircle pills, score
 *     cards, badges) -- still one click, but pdf-lib's primitives
 *     fundamentally cannot reproduce the reference's rounded corners, exact
 *     fonts, or exact colours -- confirmed not good enough by direct
 *     comparison against "Detailed Analysis Full Report.html".
 *  3. The same HTML downloaded directly as a .html file (file-saver) --
 *     pixel-identical content, but left the print step for the user to do
 *     later by hand (open the file, then Ctrl+P), rather than landing them
 *     straight in the print dialog from the click.
 * This version is (3)'s HTML/CSS (unchanged, still pixel-identical to the
 * reference -- not an approximation) with (an earlier attempt's)
 * open-a-window-and-print delivery, fixed against the popup-blocker bug
 * that broke that attempt originally: window.open() happens SYNCHRONOUSLY,
 * before any `await`, because opening it after the data fetch breaks the
 * "direct response to a user gesture" association most browsers require --
 * crossing that gap gets the popup silently blocked with no error at all,
 * which is exactly what "click Download, nothing happens" turned out to be
 * last time this delivery mechanism was tried.
 *
 * Deliberately not a pixel-identical copy of the reference in three places,
 * all intentional deviations from stale content in that reference file:
 *  1. Responsibility rows' "Resume"/"Evidence" columns show the REAL
 *     citation TalentOS found and its REAL stored label, not the reference
 *     file's duplicated achievement text in both columns (that file mocked
 *     up a shape the current backend doesn't produce -- TalentOS stores one
 *     citation + one categorical label per responsibility, never two
 *     separate strings).
 *  2. The "Weighted sum × coherence = X% · Informational (weight 0): ..."
 *     footer line is left out, matching the on-screen report -- that line
 *     was explicitly removed from the on-screen version earlier the same
 *     day ("remove that ... its taking too space") and the reference file
 *     predates that removal.
 *  3. The footer omits the Foundry-sourced "Candidate CD######" code
 *     (resumes.candidate_id) the reference file includes -- this report is
 *     pure TalentOS data throughout (standing rule this whole build), and
 *     that code is the one AIRecruit/Foundry-owned value with no TalentOS
 *     equivalent to substitute.
 */
import { talentosApi } from './talentosApi'
import type {
  TalentosDimension,
  TalentosGateStatus,
  TalentosMatchRun,
  TalentosRecommendation,
  TalentosResponsibilityComparison,
  TalentosSkillComparison,
  TalentosSkillVerdict,
} from './talentosApi'
import {
  dimensionLabel,
  experienceStatusLabel,
  experienceStatusSymbol,
  gateOutcomeLabel,
  gateOutcomeSymbol,
  NEUTRAL_PILL_SYMBOL,
  percentText,
  recommendationLabel,
  recommendationPillSymbol,
  responsibilityLabelText,
  skillCategoryLabel,
  skillVerdictLabel,
  skillVerdictSymbol,
  hasGateData,
} from './talentosLabels'

interface PillColors {
  bg: string
  fg: string
}

// Same palette as the on-screen pills (talentosLabels.ts's own
// bg-green-100/amber-50/red-100/blue-100/slate-100 + -700/-600 text), as
// literal CSS hex here instead of Tailwind classes.
const GREEN: PillColors = { bg: '#dcfce7', fg: '#15803d' }
const AMBER: PillColors = { bg: '#fffbeb', fg: '#b45309' }
const RED: PillColors = { bg: '#fee2e2', fg: '#b91c1c' }
const BLUE: PillColors = { bg: '#dbeafe', fg: '#1d4ed8' }
const SLATE: PillColors = { bg: '#f1f5f9', fg: '#475569' }

function recommendationColors(recommendation: TalentosRecommendation | null, gateStatus: TalentosGateStatus): PillColors {
  if (!recommendation || gateStatus === 'ineligible') return RED
  switch (recommendation) {
    case 'strongly_recommended':
    case 'recommended':
    case 'strong_hire':
      return GREEN
    case 'not_recommended':
      return RED
    case 'consider_with_caution':
      return AMBER
    default:
      return SLATE
  }
}

function experienceColors(gateStatus: TalentosGateStatus): PillColors {
  switch (gateStatus) {
    case 'qualified':
      return GREEN
    case 'over_qualified':
      return AMBER
    case 'ineligible':
      return RED
    default:
      return SLATE
  }
}

function gateOutcomeColors(status: string | undefined): PillColors {
  switch (status) {
    case 'passed':
      return GREEN
    case 'failed':
      return RED
    default:
      return SLATE
  }
}

function skillVerdictColors(verdict: TalentosSkillVerdict): PillColors {
  switch (verdict) {
    case 'matched':
      return GREEN
    case 'partial':
      return AMBER
    case 'not_found':
      return RED
    case 'insufficient_evidence':
      return BLUE
    default:
      return SLATE
  }
}

function esc(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function pill(text: string, colors: PillColors): string {
  return `<span class="pill" style="background:${colors.bg};color:${colors.fg}">${esc(text)}</span>`
}

/** `**bold**` + `- ` bullet lines -> real `<b>`/`<ul><li>` HTML. Mirrors the
 *  same markdown subset display_reasoning ever uses (see TalentosDimension's
 *  own doc comment) -- a full markdown parser would be overkill for two
 *  constructs. */
function mdToHtml(text: string): string {
  const boldToHtml = (s: string) =>
    esc(s)
      .split('**')
      .map((part, i) => (i % 2 === 1 ? `<b>${part}</b>` : part))
      .join('')
  const lines = text.split('\n').filter((l) => l.trim() !== '')
  const isBulletList = lines.length > 0 && lines.every((l) => /^\s*-\s+/.test(l))
  if (isBulletList) {
    const items = lines.map((l) => `<li>${boldToHtml(l.replace(/^\s*-\s+/, ''))}</li>`).join('')
    return `<ul>${items}</ul>`
  }
  return boldToHtml(text)
}

/** Mirrors TalentosMatchReport.tsx's comparisonRows construction exactly --
 *  same row set (skills, Total experience, Seniority), same wording. No
 *  Mandatory/Domain/Responsibility rows: those live in their own sections
 *  below, matching the on-screen 2026-09-24 layout exactly. */
function buildSkillTableRows(run: TalentosMatchRun): string {
  const rows: string[] = []

  for (const skill of run.skill_comparison ?? []) {
    const jdRequirement =
      (skill.category === 'required' ? 'Required' : 'Preferred') +
      (skill.minimum_years != null ? ` · ${skill.minimum_years}+ yrs` : '')
    const snippet = (skill as TalentosSkillComparison).evidence_snippet
    rows.push(
      `<tr><td><b>${esc(skill.skill)}</b></td><td>${esc(skillCategoryLabel(skill.category))}</td>` +
        `<td>${esc(jdRequirement)}</td><td>${skill.verdict === 'not_found' ? '—' : esc(skill.skill)}</td>` +
        `<td>${pill(`${skillVerdictSymbol(skill.verdict)} ${skillVerdictLabel(skill.verdict)}`, skillVerdictColors(skill.verdict))}</td>` +
        `<td>${esc(skill.evidence)}${snippet ? `<div class="m"><i>"${esc(snippet)}"</i></div>` : ''}</td></tr>`,
    )
  }

  if (run.job) {
    const min = run.job.min_experience_years
    const max = run.job.max_experience_years
    const jdRequirement =
      min != null && max != null
        ? `${min}-${max} years`
        : min != null
          ? `${min}+ years`
          : max != null
            ? `Up to ${max} years`
            : '—'
    rows.push(
      `<tr><td><b>Total experience</b></td><td>Experience</td><td>${esc(jdRequirement)}</td>` +
        `<td>${run.candidate.total_experience_years != null ? esc(`${run.candidate.total_experience_years.toFixed(1)} years`) : '—'}</td>` +
        `<td>${pill(`${experienceStatusSymbol(run.gate_status)} ${experienceStatusLabel(run.gate_status)}`, experienceColors(run.gate_status))}</td>` +
        `<td>${esc(run.gate_reason) || '—'}</td></tr>`,
    )
    rows.push(
      `<tr><td><b>Seniority</b></td><td>Experience</td><td>${esc(run.job.seniority_level) || '—'}</td>` +
        `<td>${esc(run.candidate.current_title) || '—'}</td>` +
        `<td>${pill(`${NEUTRAL_PILL_SYMBOL} Informational`, SLATE)}</td>` +
        `<td>Current title from resume</td></tr>`,
    )
  }

  return rows.join('')
}

function buildResponsibilityRows(items: TalentosResponsibilityComparison[]): string {
  return items
    .map(
      (item, i) =>
        `<tr><td>${i + 1}</td><td><b>${esc(item.responsibility)}</b></td>` +
        `<td>${item.citation ? esc(item.citation) : '—'}</td>` +
        `<td>${esc(responsibilityLabelText(item.label))}</td></tr>`,
    )
    .join('')
}

function buildDimensionRows(dimensions: TalentosDimension[]): string {
  return dimensions
    .map((d) => {
      const percent = Math.round(d.raw_score * 100)
      const weight = Math.round(d.weight_applied * 100)
      const body = d.display_reasoning ?? d.reasoning ?? ''
      return (
        `<tr><td><b>${esc(dimensionLabel(d.dimension_key))}</b></td><td>${percent}%</td><td>${weight}%</td>` +
        `<td>${mdToHtml(body)}</td></tr>`
      )
    })
    .join('')
}

function buildReportHtml(run: TalentosMatchRun, fallbackName?: string): string {
  const name = run.candidate.full_name || fallbackName || 'Candidate'
  const recColors = recommendationColors(run.recommendation, run.gate_status)
  const percentLabel = run.display_percent === null ? '—' : `${percentText(run.display_percent)}%`
  const ringColor = run.display_percent === null ? '#cbd5e1' : recColors.fg
  const requiredCoverage = run.skills_threshold_evidence
  const shortLine = requiredCoverage
    ? `${requiredCoverage.matched_count} of ${requiredCoverage.jd_skill_count} required skills matched` +
      (requiredCoverage.missing_skills.length > 0 ? `; ${requiredCoverage.missing_skills.length} not found in the resume.` : '.')
    : ''

  const identityBits = [
    run.candidate.current_title,
    run.candidate.current_company,
    run.candidate.total_experience_years != null ? `${run.candidate.total_experience_years.toFixed(1)} years` : null,
    run.candidate.location,
  ]
    .filter(Boolean)
    .join(' · ')
  const jobBits = [run.job?.role_title, run.job?.seniority_level, run.job?.domain].filter(Boolean).join(' · ')

  const showGates = run.gate_status !== 'ineligible' && hasGateData(run)
  const tileCards = [
    { label: 'OVERALL SCORE', value: `<b style="font-size:16px">${esc(percentLabel)}</b>` },
    {
      label: 'RECOMMENDATION',
      value: pill(`${recommendationPillSymbol(run.recommendation, run.gate_status)} ${recommendationLabel(run.recommendation, run.gate_status)}`, recColors),
    },
    {
      label: 'EXPERIENCE GATE',
      value: pill(`${experienceStatusSymbol(run.gate_status)} ${experienceStatusLabel(run.gate_status)}`, experienceColors(run.gate_status)),
    },
  ]
  if (showGates) {
    tileCards.push({
      label: 'REQUIRED-SKILLS GATE',
      value: pill(`${gateOutcomeSymbol(run.skills_threshold_status)} ${gateOutcomeLabel(run.skills_threshold_status)}`, gateOutcomeColors(run.skills_threshold_status)),
    })
  }
  const tilesHtml = tileCards
    .map((c) => `<div class="tile"><div class="l">${c.label}</div>${c.value}</div>`)
    .join('')

  const domainDim = run.dimensions.find((d) => d.dimension_key === 'domain')
  const candidateDomain = run.candidate.domain ?? []
  const hasDomainCard = !!(run.job?.domain || candidateDomain.length > 0)
  const responsibilityItems = run.responsibility_comparison ?? []
  const respCounts = new Map<string, number>()
  for (const item of responsibilityItems) {
    const label = responsibilityLabelText(item.label)
    respCounts.set(label, (respCounts.get(label) ?? 0) + 1)
  }
  const respStatusSummary = Array.from(respCounts.entries())
    .map(([label, count]) => `${count} ${label.toLowerCase()}`)
    .join(' · ')

  const skillRows = run.skill_comparison ?? []
  const requiredMatched = skillRows.filter((r) => r.category === 'required' && r.verdict !== 'not_found').length
  const requiredTotal = skillRows.filter((r) => r.category === 'required').length
  const notFoundCount = skillRows.filter((r) => r.verdict === 'not_found').length
  const insufficientCount = skillRows.filter((r) => r.verdict === 'insufficient_evidence').length

  const scored = run.dimensions.filter((d) => d.weight_applied > 0)
  const coverage = run.skills_threshold_evidence ?? null

  return `<!doctype html><html><head><meta charset="utf-8" /><title>Detailed Analysis — ${esc(name)}</title><style>
@page{size:A4;margin:14mm}
*{box-sizing:border-box}
body{font-family:Inter,system-ui,sans-serif;color:#0f172a;margin:0;font-size:12px;line-height:1.5}
h1{font-size:20px;margin:0 0 2px}
h2{display:flex;align-items:center;gap:10px;font-size:14px;margin:22px 0 10px;page-break-after:avoid}
h3{font-size:12.5px;margin:16px 0 8px}
.n{width:22px;height:22px;border-radius:50%;background:#dbeafe;color:#1d4ed8;font-size:11px;display:inline-flex;align-items:center;justify-content:center}
.m{color:#64748b;font-size:11px}
.head{display:flex;justify-content:space-between;gap:16px;align-items:center;border-bottom:1px solid #e2e8f0;padding-bottom:14px}
.ring{width:70px;height:70px;border-radius:50%;border:6px solid #16a34a;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;flex-shrink:0}
.tiles{display:grid;grid-template-columns:repeat(${tileCards.length},1fr);gap:8px;margin-top:14px}
.tile{border:1px solid #e2e8f0;border-radius:8px;padding:8px 10px}
.tile .l{font-size:9.5px;letter-spacing:.08em;color:#94a3b8;font-weight:700;margin-bottom:4px}
.pill{display:inline-block;padding:1px 8px;border-radius:6px;font-size:11px;font-weight:600;white-space:nowrap}
table{width:100%;border-collapse:collapse;border:1px solid #eef2f7}
th{text-align:left;font-size:11px;color:#334155;background:#f8fafc;padding:7px 8px;border-bottom:1px solid #eef2f7}
td{padding:7px 8px;border-top:1px solid #eef2f7;vertical-align:top}
tr{page-break-inside:avoid}
ul{margin:0;padding-left:16px}
li{margin:0 0 4px}
.box{border:1px solid #eef2f7;border-radius:8px;padding:10px 12px;margin-bottom:8px;page-break-inside:avoid}
.k{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
.bar{position:relative;height:7px;border-radius:999px;background:#eef2f7;margin-top:8px}
.bar i{position:absolute;left:0;top:0;bottom:0;border-radius:999px}
footer{margin-top:22px;border-top:1px solid #e2e8f0;padding-top:8px;font-size:10px;color:#94a3b8}
</style></head><body>

<div class="head">
  <div>
    <div class="m" style="letter-spacing:.08em;font-weight:700">DETAILED ANALYSIS</div>
    <h1>${esc(name)}</h1>
    <div class="m">${esc(identityBits)}</div>
    ${jobBits ? `<div class="m">Job: <b style="color:#0f172a">${esc(run.job?.role_title)}</b> · ${esc([run.job?.seniority_level, run.job?.domain].filter(Boolean).join(' · '))}</div>` : ''}
  </div>
  <div style="display:flex;align-items:center;gap:12px">
    <div class="ring" style="border-color:${ringColor}">${esc(percentLabel)}</div>
    <div>
      ${pill(`${recommendationPillSymbol(run.recommendation, run.gate_status)} ${recommendationLabel(run.recommendation, run.gate_status)}`, recColors)}
      ${shortLine ? `<div class="m" style="margin-top:4px;max-width:220px">${esc(shortLine)}</div>` : ''}
    </div>
  </div>
</div>

<div class="tiles">${tilesHtml}</div>

${
  run.narrative_summary
    ? `<h2><span class="n">1</span>Summary</h2><p style="margin:0">${esc(run.narrative_summary)}</p>`
    : ''
}

${
  skillRows.length > 0 || run.job
    ? `<h2><span class="n">2</span>Skills Matrix</h2>
<table><thead><tr><th>Requirement / Skill</th><th>Category</th><th>JD Requirement</th><th>Resume</th><th>Match</th><th>Evidence</th></tr></thead>
<tbody>${buildSkillTableRows(run)}</tbody></table>`
    : ''
}

${
  hasDomainCard || responsibilityItems.length > 0
    ? `<h3>Domain &amp; Responsibilities</h3>
${
  hasDomainCard
    ? `<div class="box"><b>Domain fit</b> — JD: <b>${esc(run.job?.domain) || '—'}</b> → Resume: <b>${esc(candidateDomain.join(', ')) || '—'}</b>
  <div style="margin-top:6px"><span class="m">EVIDENCE</span><div>${esc(domainDim?.reasoning) || '—'}</div></div>
</div>`
    : ''
}
${
  responsibilityItems.length > 0
    ? `<div class="m" style="margin:10px 0 6px"><b style="color:#0f172a">Responsibilities</b> · ${responsibilityItems.length} · ${esc(respStatusSummary)}</div>
<table><thead><tr><th style="width:4%">#</th><th style="width:32%">JD Responsibility</th><th>Resume</th><th>Evidence</th></tr></thead>
<tbody>${buildResponsibilityRows(responsibilityItems)}</tbody></table>`
    : ''
}`
    : ''
}

${
  skillRows.length > 0
    ? `<h2><span class="n">3</span>Key Takeaways</h2>
<div class="k">
  <div class="box" style="background:#f0fdf4;border-color:#bbf7d0"><b style="font-size:16px;color:#15803d">✓ ${requiredMatched} / ${requiredTotal}</b><div class="m">Required skills matched</div></div>
  <div class="box" style="background:#fef2f2;border-color:#fecaca"><b style="font-size:16px;color:#b91c1c">✕ ${notFoundCount}</b><div class="m">Skills not found</div></div>
  <div class="box" style="background:#eff6ff;border-color:#bfdbfe"><b style="font-size:16px;color:#1d4ed8">? ${insufficientCount}</b><div class="m">Insufficient evidence</div></div>
</div>`
    : ''
}

${
  scored.length > 0
    ? `<h2><span class="n">4</span>Match Result</h2>
<table><thead><tr><th>Dimension</th><th>Score</th><th>Weight</th><th>Key insight</th></tr></thead>
<tbody>${buildDimensionRows(scored)}</tbody></table>`
    : ''
}

<h2><span class="n">5</span>Gate Details</h2>
<div class="box">
  <div style="display:flex;justify-content:space-between;gap:10px"><b>1. Experience Gate</b>${pill(`${experienceStatusSymbol(run.gate_status)} ${experienceStatusLabel(run.gate_status)}`, experienceColors(run.gate_status))}</div>
  <div style="margin-top:4px">${esc(run.gate_reason) || '—'}</div>
</div>
${
  showGates
    ? `<div class="box">
  <div style="display:flex;justify-content:space-between;gap:10px"><b>2. Required-Skills Threshold Gate</b>${pill(`${gateOutcomeSymbol(run.skills_threshold_status)} ${gateOutcomeLabel(run.skills_threshold_status)}`, gateOutcomeColors(run.skills_threshold_status))}</div>
  ${coverage ? `<div style="margin-top:4px">${coverage.matched_count} of ${coverage.jd_skill_count} required skills evidenced (${Math.round(coverage.coverage * 100)}%)</div>
  <div class="bar"><i style="width:${Math.round(coverage.coverage * 100)}%;background:${run.skills_threshold_status === 'failed' ? '#dc2626' : '#16a34a'}"></i></div>
  ${coverage.missing_skills.length > 0 ? `<div class="m" style="margin-top:6px"><b style="color:#0f172a">Missing:</b> ${esc(coverage.missing_skills.join(', '))}</div>` : ''}` : ''}
</div>`
    : ''
}

<footer>Scoring engine ${esc(run.scoring_engine_version)} · Generated ${new Date().toLocaleString()}</footer>

</body></html>`
}

/**
 * Fetch the newest TalentOS run for (jobId, resumeId) and trigger the
 * browser's native print dialog for it, right on the current page -- no
 * new tab, no separate window to switch to. The report is rendered into a
 * hidden iframe appended to this document; printing an iframe's own
 * contentWindow prints only that iframe's content (not the host page
 * behind it), which is what lets this stay a same-page action instead of
 * a new window. The iframe is removed again once printing is done.
 * Returns false when this candidate has no TalentOS run yet, so the
 * caller can tell the user why -- there is no honest report to generate.
 */
export async function downloadTalentosMatchReportPdf(
  jobId: string,
  resumeId: string,
  fallbackCandidateName?: string,
): Promise<boolean> {
  const run = await talentosApi.getLatestMatch(jobId, resumeId)
  if (!run) return false

  const html = buildReportHtml(run, fallbackCandidateName)

  const iframe = document.createElement('iframe')
  // Off-screen rather than display:none -- a display:none iframe's
  // document never lays out, and some browsers refuse to print content
  // that was never laid out.
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.style.visibility = 'hidden'
  document.body.appendChild(iframe)

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
  }

  const iframeWindow = iframe.contentWindow
  const iframeDoc = iframe.contentDocument
  if (!iframeWindow || !iframeDoc) {
    cleanup()
    return false
  }
  iframeDoc.open()
  iframeDoc.write(html)
  iframeDoc.close()

  // Let the iframe finish laying out before printing -- calling print()
  // in the same tick can fire before styles/fonts have applied. Exactly
  // one trigger: `onload` when the browser fires it, a timed fallback
  // otherwise (some browsers don't fire `load` for a document.write()'d
  // frame) -- guarded so only whichever fires first actually opens the
  // dialog, never both. `afterprint` removes the iframe once the dialog
  // is dismissed (Save or Cancel); a longer timeout is a backstop in case
  // a browser never fires it.
  let printed = false
  const printOnce = () => {
    if (printed) return
    printed = true
    iframeWindow.focus()
    iframeWindow.print()
  }
  iframeWindow.onload = printOnce
  setTimeout(printOnce, 300)
  iframeWindow.onafterprint = cleanup
  setTimeout(cleanup, 60000)

  return true
}
